import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Settings, Save, Loader, Check, Landmark, Percent, ChevronRight, Info, Inbox, ShieldCheck, Wifi, HardDrive } from 'lucide-react';
import { parametresApi } from '../../utils/api';

// Boîte mail de recrutement (IMAP) — le mot de passe va directement au serveur (Suisse), jamais au chat/USA.
function MailRecrutementCard() {
  const [cfg, setCfg] = useState(null);
  const [pass, setPass] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { parametresApi.getMailRecrutement().then(r => setCfg(r.data)).catch(() => setCfg({ host: 'mail.infomaniak.com', port: 993, user: '', actif: false, pass_defini: false })); }, []);
  if (!cfg) return null;
  const upd = (k, v) => setCfg(c => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const { data } = await parametresApi.setMailRecrutement({ host: cfg.host, port: cfg.port, user: cfg.user, actif: cfg.actif, pass: pass || undefined });
      setCfg(data); setPass('');
      setMsg({ t: 'ok', m: 'Configuration enregistrée.' });
    } catch (e) { setMsg({ t: 'err', m: e.response?.data?.error || 'Erreur.' }); }
    setSaving(false);
  };
  const tester = async () => {
    setTesting(true); setMsg(null);
    try {
      const { data } = await parametresApi.testMailRecrutement({ host: cfg.host, port: cfg.port, user: cfg.user, pass: pass || undefined });
      setMsg(data.ok ? { t: 'ok', m: '✓ Connexion réussie à la boîte.' } : { t: 'err', m: 'Connexion échouée : ' + (data.error || '') });
    } catch (e) { setMsg({ t: 'err', m: e.response?.data?.error || 'Test impossible.' }); }
    setTesting(false);
  };

  return (
    <section className="apple-card p-5">
      <h2 className="font-semibold text-navy-800 flex items-center gap-2 mb-1"><Inbox size={17} className="text-navy-500" /> Boîte mail de recrutement</h2>
      <p className="text-[12.5px] text-navy-400 mb-3">
        Reliez la boîte <strong>recrutement@lescachettes.fr</strong> pour importer automatiquement les candidatures (et les trier poste / stage / alternance).
      </p>
      <div className="flex items-start gap-2 text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mb-4">
        <ShieldCheck size={15} className="mt-0.5 flex-shrink-0" />
        <span>Le mot de passe est envoyé <strong>directement à votre serveur (en Suisse)</strong>, chiffré, et n'est jamais réaffiché. Il ne passe ni par le chat ni par les États-Unis.</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-sm">Serveur IMAP<input className="form-input mt-1 text-sm" value={cfg.host || ''} onChange={e => upd('host', e.target.value)} /></label>
        <label className="text-sm">Port<input className="form-input mt-1 text-sm" value={cfg.port || 993} onChange={e => upd('port', e.target.value)} /></label>
        <label className="text-sm">Adresse (identifiant)<input className="form-input mt-1 text-sm" value={cfg.user || ''} onChange={e => upd('user', e.target.value)} placeholder="recrutement@lescachettes.fr" /></label>
        <label className="text-sm">Mot de passe d'application
          <input type="password" className="form-input mt-1 text-sm" value={pass} onChange={e => setPass(e.target.value)}
            placeholder={cfg.pass_defini ? '•••••••• (déjà enregistré — laissez vide pour garder)' : 'Collez le mot de passe'} />
        </label>
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm text-navy-700">
        <input type="checkbox" checked={!!cfg.actif} onChange={e => upd('actif', e.target.checked)} className="w-4 h-4 accent-navy-700" />
        Activer le relevé automatique des candidatures
      </label>
      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <button onClick={save} disabled={saving} className="apple-btn-primary">{saving ? <Loader size={15} className="animate-spin" /> : <Save size={15} />} Enregistrer</button>
        <button onClick={tester} disabled={testing} className="apple-btn-secondary">{testing ? <Loader size={15} className="animate-spin" /> : <Wifi size={15} />} Tester la connexion</button>
        {msg && <span className={`text-[13px] flex items-center gap-1 ${msg.t === 'err' ? 'text-rose-600' : 'text-emerald-600'}`}>{msg.t === 'ok' && <Check size={14} />}{msg.m}</span>}
      </div>
    </section>
  );
}

// Sauvegarde hors-serveur vers kDrive Infomaniak (Suisse) — mot de passe d'application direct au serveur, jamais au chat.
function KdriveBackupCard() {
  const [cfg, setCfg] = useState(null);
  const [pass, setPass] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { parametresApi.getKdrive().then(r => setCfg(r.data)).catch(() => setCfg({ url: '', user: '', actif: false, pass_defini: false })); }, []);
  if (!cfg) return null;
  const upd = (k, v) => setCfg(c => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const { data } = await parametresApi.setKdrive({ url: cfg.url, user: cfg.user, actif: cfg.actif, pass: pass || undefined });
      setCfg(data); setPass('');
      setMsg({ t: 'ok', m: 'Configuration enregistrée.' });
    } catch (e) { setMsg({ t: 'err', m: e.response?.data?.error || 'Erreur.' }); }
    setSaving(false);
  };
  const tester = async () => {
    setTesting(true); setMsg(null);
    try {
      const { data } = await parametresApi.testKdrive({ pass: pass || undefined });
      setMsg({ t: 'ok', m: '✓ ' + (data.message || 'Connexion kDrive réussie.') });
    } catch (e) { setMsg({ t: 'err', m: e.response?.data?.error || 'Test impossible.' }); }
    setTesting(false);
  };

  return (
    <section className="apple-card p-5">
      <h2 className="font-semibold text-navy-800 flex items-center gap-2 mb-1"><HardDrive size={17} className="text-navy-500" /> Sauvegarde hors-serveur (kDrive)</h2>
      <p className="text-[12.5px] text-navy-400 mb-3">
        Copie automatique des sauvegardes hebdomadaires vers votre <strong>kDrive Infomaniak</strong> (en Suisse) — protection en cas de perte du serveur.
      </p>
      <div className="flex items-start gap-2 text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mb-3">
        <ShieldCheck size={15} className="mt-0.5 flex-shrink-0" />
        <span>Le mot de passe d'application est envoyé <strong>directement à votre serveur (Suisse)</strong>, chiffré, jamais réaffiché ni transmis au chat.</span>
      </div>
      <div className="flex items-start gap-2 text-[12px] text-navy-500 bg-cream-50 border border-cream-200 rounded-lg px-3 py-2 mb-4">
        <Info size={15} className="mt-0.5 flex-shrink-0" />
        <span>Dans le Manager Infomaniak : créez un <strong>mot de passe d'application</strong> (Profil → Sécurité) et créez un dossier kDrive (ex. « Sauvegardes Hello Georges »). L'URL WebDAV ressemble à <code>https://&lt;id&gt;.connect.kdrive.infomaniak.com/Sauvegardes-Hello-Georges/</code>.</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-sm sm:col-span-2">URL du dossier kDrive (WebDAV)
          <input className="form-input mt-1 text-sm" value={cfg.url || ''} onChange={e => upd('url', e.target.value)} placeholder="https://123456.connect.kdrive.infomaniak.com/Sauvegardes-Hello-Georges/" />
        </label>
        <label className="text-sm">Identifiant (e-mail Infomaniak)<input className="form-input mt-1 text-sm" value={cfg.user || ''} onChange={e => upd('user', e.target.value)} placeholder="vous@exemple.com" /></label>
        <label className="text-sm">Mot de passe d'application
          <input type="password" className="form-input mt-1 text-sm" value={pass} onChange={e => setPass(e.target.value)}
            placeholder={cfg.pass_defini ? '•••••••• (déjà enregistré — laissez vide pour garder)' : "Collez le mot de passe d'application"} />
        </label>
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm text-navy-700">
        <input type="checkbox" checked={!!cfg.actif} onChange={e => upd('actif', e.target.checked)} className="w-4 h-4 accent-navy-700" />
        Activer la copie automatique vers kDrive
      </label>
      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <button onClick={save} disabled={saving} className="apple-btn-primary">{saving ? <Loader size={15} className="animate-spin" /> : <Save size={15} />} Enregistrer</button>
        <button onClick={tester} disabled={testing} className="apple-btn-secondary">{testing ? <Loader size={15} className="animate-spin" /> : <Wifi size={15} />} Tester la connexion</button>
        {msg && <span className={`text-[13px] flex items-center gap-1 ${msg.t === 'err' ? 'text-rose-600' : 'text-emerald-600'}`}>{msg.t === 'ok' && <Check size={14} />}{msg.m}</span>}
      </div>
    </section>
  );
}

const MODES = [
  { key: 'paje_micro_creche', label: 'PAJE micro-crèche (CMG)', desc: 'Tarif horaire libre, aide CMG versée aux familles par la CAF.' },
  { key: 'psu', label: 'PSU (Prestation de Service Unique)', desc: 'Participation familiale au barème CNAF (taux d\'effort selon ressources).' },
];

// Champs du barème CMG (clé → libellé)
const PLAFONDS = [
  ['plafond_bas_1', 'plafond_haut_1', '1 enfant'],
  ['plafond_bas_2', 'plafond_haut_2', '2 enfants'],
  ['plafond_bas_3', 'plafond_haut_3', '3 enfants'],
];
const CMG = [
  ['cmg_m3_t1', 'cmg_m3_t2', 'cmg_m3_t3', 'Moins de 3 ans'],
  ['cmg_36_t1', 'cmg_36_t2', 'cmg_36_t3', 'De 3 à 6 ans'],
];

function Num({ value, onChange, suffix = '€' }) {
  return (
    <div className="relative">
      <input type="number" step="0.01" value={value ?? ''} onChange={e => onChange(e.target.value)}
        className="w-full border border-cream-300 rounded-lg pl-2 pr-6 py-1.5 text-sm text-right" />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-navy-300">{suffix}</span>
    </div>
  );
}

export default function ParametresPage() {
  const [structures, setStructures] = useState([]);
  const [bareme, setBareme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingMode, setSavingMode] = useState(null);
  const [savingBareme, setSavingBareme] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    parametresApi.get()
      .then(r => { setStructures(r.data.structures || []); setBareme(r.data.bareme || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setMode = async (structureId, mode) => {
    setSavingMode(structureId);
    try {
      await parametresApi.setFinancement(structureId, mode);
      setStructures(s => s.map(x => x.id === structureId ? { ...x, mode_financement: mode } : x));
    } catch { /* noop */ }
    setSavingMode(null);
  };

  const b = (k) => (bareme ? bareme[k] : '');
  const setB = (k, v) => setBareme(prev => ({ ...prev, [k]: v }));

  const saveBareme = async () => {
    setSavingBareme(true); setMsg('');
    try {
      const r = await parametresApi.updateBareme({ ...bareme, date_effet: bareme.date_effet || new Date().toISOString().slice(0, 10) });
      setBareme(r.data.bareme);
      setMsg('Nouveau barème enregistré — il s\'applique désormais à tous les devis.');
      setTimeout(() => setMsg(''), 6000);
    } catch (e) { setMsg(e.response?.data?.error || 'Erreur'); }
    setSavingBareme(false);
  };

  if (loading) return <div className="p-10 text-center text-navy-300">Chargement…</div>;

  return (
    <div className="-m-6 p-6 min-h-full bg-cream-200">
      <div className="max-w-4xl mx-auto pb-16 space-y-6">
        <div>
          <h1 className="font-serif text-[32px] leading-tight text-navy-900 flex items-center gap-2"><Settings size={26} className="text-gold-600" /> Paramètres</h1>
          <div className="w-12 h-0.5 bg-gold-400 rounded-full mt-2 mb-2.5" />
          <p className="text-navy-400 text-[14px]">Financement, tarifs et barèmes de chaque structure. Réservé à l'administrateur.</p>
        </div>

        {/* Mode de financement */}
        <section className="apple-card p-5">
          <h2 className="font-semibold text-navy-800 flex items-center gap-2 mb-1"><Landmark size={17} className="text-navy-500" /> Mode de financement par structure</h2>
          <p className="text-[12.5px] text-navy-400 mb-4">Détermine le mode de calcul des tarifs et les mentions des devis (aide CMG pour la PAJE, participation familiale pour la PSU).</p>
          <div className="space-y-3">
            {structures.map(s => (
              <div key={s.id} className="flex items-center justify-between gap-3 flex-wrap border border-cream-300/60 rounded-xl px-4 py-3">
                <span className="font-medium text-navy-800">{s.nom}</span>
                <div className="flex items-center gap-2">
                  {savingMode === s.id && <Loader size={14} className="animate-spin text-navy-300" />}
                  <div className="seg inline-flex rounded-lg overflow-hidden border border-cream-300">
                    {MODES.map(m => (
                      <button key={m.key} onClick={() => setMode(s.id, m.key)} title={m.desc}
                        className={`px-3 py-1.5 text-[12.5px] font-medium transition-colors ${(s.mode_financement || 'paje_micro_creche') === m.key ? 'bg-navy-700 text-white' : 'bg-white text-navy-500 hover:bg-cream-50'}`}>
                        {m.key === 'psu' ? 'PSU' : 'PAJE micro-crèche'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Barème CMG */}
        <section className="apple-card p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <h2 className="font-semibold text-navy-800 flex items-center gap-2"><Percent size={17} className="text-gold-600" /> Barème CMG micro-crèche</h2>
            {bareme?.date_effet && <span className="text-[11px] text-navy-300">Version en vigueur au {String(bareme.date_effet).split('-').reverse().join('/')}</span>}
          </div>
          <div className="flex items-start gap-2 text-[12px] text-navy-500 bg-cream-100 border border-cream-300/60 rounded-lg px-3 py-2 mb-4">
            <Info size={14} className="flex-shrink-0 mt-0.5 text-gold-600" />
            <span>Ces montants (versés aux familles par la CAF) apparaissent sur les devis PAJE. À chaque <strong>revalorisation</strong>, mettez-les à jour ici : une nouvelle version est créée et <strong>tous les devis s'actualisent automatiquement</strong> (l'ancienne reste dans l'historique).</span>
          </div>

          {/* Date d'effet + libellé */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-[12px] text-navy-500">Date d'effet</label>
              <input type="date" value={b('date_effet') || ''} onChange={e => setB('date_effet', e.target.value)} className="w-full border border-cream-300 rounded-lg px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[12px] text-navy-500">Libellé</label>
              <input type="text" value={b('label') || ''} onChange={e => setB('label', e.target.value)} placeholder="ex. Barème 2026" className="w-full border border-cream-300 rounded-lg px-2 py-1.5 text-sm" />
            </div>
          </div>

          {/* Plafonds de ressources */}
          <p className="text-[12px] font-semibold text-navy-600 mb-2">Plafonds de ressources annuelles (revenu fiscal de référence)</p>
          <div className="space-y-2 mb-4">
            <div className="grid grid-cols-12 gap-2 text-[11px] text-navy-400 px-1">
              <span className="col-span-4">Enfants à charge</span><span className="col-span-4">Ressources inférieures à</span><span className="col-span-4">Ressources supérieures à</span>
            </div>
            {PLAFONDS.map(([bas, haut, label]) => (
              <div key={label} className="grid grid-cols-12 gap-2 items-center">
                <span className="col-span-4 text-sm text-navy-700">{label}</span>
                <div className="col-span-4"><Num value={b(bas)} onChange={v => setB(bas, v)} /></div>
                <div className="col-span-4"><Num value={b(haut)} onChange={v => setB(haut, v)} /></div>
              </div>
            ))}
          </div>

          {/* Montants CMG mensuels */}
          <p className="text-[12px] font-semibold text-navy-600 mb-2">Montant CMG mensuel maximum versé par la CAF (micro-crèche)</p>
          <div className="space-y-2 mb-4">
            <div className="grid grid-cols-12 gap-2 text-[11px] text-navy-400 px-1">
              <span className="col-span-3">Âge</span><span className="col-span-3">Ressources faibles</span><span className="col-span-3">Moyennes</span><span className="col-span-3">Élevées</span>
            </div>
            {CMG.map(([t1, t2, t3, label]) => (
              <div key={label} className="grid grid-cols-12 gap-2 items-center">
                <span className="col-span-3 text-sm text-navy-700">{label}</span>
                <div className="col-span-3"><Num value={b(t1)} onChange={v => setB(t1, v)} /></div>
                <div className="col-span-3"><Num value={b(t2)} onChange={v => setB(t2, v)} /></div>
                <div className="col-span-3"><Num value={b(t3)} onChange={v => setB(t3, v)} /></div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-[12px] text-navy-500">Crédit d'impôt annuel / enfant</label>
              <Num value={b('credit_impot_annuel')} onChange={v => setB('credit_impot_annuel', v)} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={saveBareme} disabled={savingBareme} className="apple-btn-primary">
              {savingBareme ? <Loader size={15} className="animate-spin" /> : <Save size={15} />} Enregistrer le barème
            </button>
            {msg && <span className="text-[13px] text-green-600 flex items-center gap-1"><Check size={14} /> {msg}</span>}
          </div>
        </section>

        {/* Boîte mail de recrutement (IMAP) */}
        <MailRecrutementCard />
        <KdriveBackupCard />

        {/* Grille tarifaire */}
        <section className="apple-card p-5">
          <h2 className="font-semibold text-navy-800 flex items-center gap-2 mb-1"><Settings size={17} className="text-navy-500" /> Grille tarifaire</h2>
          <p className="text-[12.5px] text-navy-400 mb-3">Les tarifs horaires par tranche d'heures, par structure.</p>
          <Link to="/tarifs" className="inline-flex items-center gap-1.5 text-navy-700 font-medium hover:text-navy-900 text-sm">
            Gérer la grille tarifaire <ChevronRight size={16} />
          </Link>
        </section>
      </div>
    </div>
  );
}
